import type { SavedDeckSlot } from '../account/decks'
import {
  CARD_FOIL_IDS_BEST_FIRST,
  asCardFoil,
  isCardFoilId,
  type CardFoilId,
} from '../types/foil'
import {
  foilCount,
  ownedCount,
  type CopiesMap,
  type FoilCounts,
} from './owned'

/** Copies of one name that recycling must leave in the album. */
export const RECYCLE_MIN_KEEP = 3

/** Coins paid for one recycled print. One Stormwake stays under a 20-coin pack. */
export const RECYCLE_COIN_BY_FOIL: Record<CardFoilId, number> = {
  common: 1,
  rare: 3,
  super: 5,
  ultra: 8,
  ghost: 8,
  helios: 12,
  nightglass: 18,
  stormwake: 19,
}

export type RecycleLine = {
  cardId: string
  foil: CardFoilId
  qty: number
}

const MAX_LINES = 4096
const MAX_QTY = 999

/**
 * Merges `{ cardId, foil, qty }` rows. Null when the body is unusable.
 */
export function parseRecycleLines(raw: unknown): RecycleLine[] | null {
  if (!Array.isArray(raw) || raw.length < 1 || raw.length > MAX_LINES) return null
  const merged = new Map<string, RecycleLine>()
  for (const item of raw) {
    if (!item || typeof item !== 'object') return null
    const rec = item as Record<string, unknown>
    if (typeof rec.cardId !== 'string' || !/^\d{1,10}$/.test(rec.cardId)) return null
    if (!isCardFoilId(rec.foil)) return null
    const qtyRaw = rec.qty
    if (!Number.isInteger(qtyRaw) || (qtyRaw as number) < 1 || (qtyRaw as number) > MAX_QTY) {
      return null
    }
    const add = qtyRaw as number
    const key = `${rec.cardId}:${rec.foil}`
    const prev = merged.get(key)
    const qty = (prev?.qty ?? 0) + add
    if (qty > MAX_QTY) return null
    merged.set(key, { cardId: rec.cardId, foil: rec.foil, qty })
  }
  return [...merged.values()]
}

/**
 * Coins for this selection, or null when it would drop a name under 3
 * or ask for a print the album does not have.
 */
export function recycleQuote(
  copies: CopiesMap,
  lines: RecycleLine[],
): number | null {
  if (lines.length < 1) return null
  const byCard = new Map<string, number>()
  let coins = 0
  for (const line of lines) {
    if (foilCount(copies, line.cardId, line.foil) < line.qty) return null
    byCard.set(line.cardId, (byCard.get(line.cardId) ?? 0) + line.qty)
    coins += RECYCLE_COIN_BY_FOIL[line.foil] * line.qty
  }
  for (const [cardId, qty] of byCard) {
    if (ownedCount(copies, cardId) - qty < RECYCLE_MIN_KEEP) return null
  }
  if (!Number.isSafeInteger(coins) || coins < 1) return null
  return coins
}

/**
 * Drops the selected prints. Does not mutate `copies`. Caller already quoted.
 */
export function removeRecycledCopies(
  copies: CopiesMap,
  lines: RecycleLine[],
): CopiesMap {
  const next: CopiesMap = { ...copies }
  for (const line of lines) {
    const bucket: FoilCounts = { ...(next[line.cardId] ?? {}) }
    const left = (bucket[line.foil] ?? 0) - line.qty
    if (left > 0) bucket[line.foil] = left
    else delete bucket[line.foil]
    if (Object.keys(bucket).length > 0) next[line.cardId] = bucket
    else delete next[line.cardId]
  }
  return next
}

/**
 * Keeps a deck slot on a print that is still owned. Slots whose foil
 * ran out move to the best print that still has a free copy.
 */
export function reassignSavedDeckFoils(
  copies: CopiesMap,
  slot: SavedDeckSlot,
): SavedDeckSlot {
  const used: Record<string, FoilCounts> = {}
  const take = (cardId: string, preferRaw: string | undefined): CardFoilId => {
    const prefer = asCardFoil(preferRaw)
    const left = (foil: CardFoilId): number =>
      foilCount(copies, cardId, foil) - (used[cardId]?.[foil] ?? 0)
    const pick = left(prefer) > 0
      ? prefer
      : CARD_FOIL_IDS_BEST_FIRST.find((foil) => left(foil) > 0) ?? prefer
    const bucket = used[cardId] ?? {}
    used[cardId] = { ...bucket, [pick]: (bucket[pick] ?? 0) + 1 }
    return pick
  }
  return {
    ...slot,
    mainFoils: slot.main.map((id, i) => take(id, slot.mainFoils[i])),
    extraFoils: slot.extra.map((id, i) => take(id, slot.extraFoils[i])),
  }
}
